import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { ViewController } from 'foundations-components/transitional/components/ViewController';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { AGGREGATION, TIME_SERIES } from 'reporting-data/constants/configTypes';
import { useMemo } from 'react';
import usePrevious from 'react-utils/hooks/usePrevious';
import { isSucceeded } from 'reporting-data/asyncData/AsyncData';
import { isTimeSeriesReport } from 'reporting-data/report/configReportGetters';
import { EDITOR_TYPES } from 'reporting-data/tsTypes/editorTypes';
// @ts-expect-error ts-migrate(7016)
import { transposeChart } from 'reporting-visualizations/visualizations/xy/xyUtils';
import useCloneTracker from '../../../hooks/useCloneTracker';
import { usePreviousDefinedValue } from '../../../hooks/usePreviousDefinedValue';
import { EVENTS } from '../../../lib/events';
import { strategy as singleObjectDimensionStrategy } from '../DimensionEditor/SingleObjectDimensionEditor';
import { getEditorSchemaByType } from '../common/editorUtils';
import { useSingleObjectReportPropertiesMap } from '../common/useSingleObjectReportPropertiesMap';
import { useIsDimensionsEditorValid } from './overtimeTotalsEditorUtils';
import { getShouldTransposeVisualization, updateIntraperiodForConfigType } from './subAppOvertimeUtils';
import { jsx as _jsx } from "react/jsx-runtime";
export function BaseOvertimeTotalsEditor({
  appUsageTracker,
  trackingApplicationName,
  trackingAssetType,
  trackingScreenName,
  report,
  strategy,
  onChange
}) {
  const tracker = useCloneTracker(appUsageTracker);
  const dimensionEditorSchema = useMemo(() => getEditorSchemaByType(report, EDITOR_TYPES.DIMENSION), [report]);
  const propertiesFetch = useSingleObjectReportPropertiesMap(report);
  const isDimensionsEditorValid = useIsDimensionsEditorValid(dimensionEditorSchema, propertiesFetch);

  // Keep track of the previous report's dimensions, whether it was changed via this control or not
  const previousTotalsDimension = usePreviousDefinedValue(strategy.getTotalsDimension(report));
  const previousDateDimension = usePreviousDefinedValue(strategy.getDateDimension(report));
  // This one is deliberately usePrevious rather than usePreviousDefinedValue to allow the user to clear the breakdown if
  // they want to
  const previousBreakdown = usePrevious(strategy.getBreakdown(report));
  if (!isDimensionsEditorValid || !dimensionEditorSchema || !isSucceeded(propertiesFetch)) {
    return null;
  }
  const {
    data: properties
  } = propertiesFetch;
  function updateReportDimension(value, newFirstDimensionPropertyName, newSecondDimensionPropertyName) {
    const shouldTransposeVisualization = getShouldTransposeVisualization(report);
    const newFirstDimension = properties === null || properties === void 0 ? void 0 : properties[newFirstDimensionPropertyName];
    const newSecondDimension = newSecondDimensionPropertyName ? properties === null || properties === void 0 ? void 0 : properties[newSecondDimensionPropertyName] : undefined;
    if (!newFirstDimension) {
      return;
    }
    let updatedReport = singleObjectDimensionStrategy.updateReport(report, [newFirstDimension, ...(newSecondDimension && newSecondDimension !== newFirstDimension ? [newSecondDimension] : [])]);
    updatedReport = updateIntraperiodForConfigType(updatedReport, value);
    if (shouldTransposeVisualization) {
      onChange(updatedReport.set('visualization', transposeChart(updatedReport.get('visualization'))));
    } else {
      onChange(updatedReport);
    }
  }
  function onToggleChange({
    target: {
      value
    }
  }) {
    tracker === null || tracker === void 0 || tracker.track(EVENTS.srvSwitchOvertimeToggle, {
      action: value,
      applicationName: trackingApplicationName,
      assetType: trackingAssetType || 'report',
      whereScreen: trackingScreenName
    });
    if (value === TIME_SERIES) {
      const newDatePropertyName = previousDateDimension || strategy.getDefaultDateDimension(report);
      const newSecondDimension = previousBreakdown;
      updateReportDimension(value, newDatePropertyName, newSecondDimension);
    } else {
      if (!dimensionEditorSchema || !properties) {
        return;
      }
      const newTotalsPropertyName = previousTotalsDimension || strategy.getDefaultTotalsDimension(report, dimensionEditorSchema, properties);
      const newSecondDimension = previousBreakdown;
      if (newTotalsPropertyName) {
        updateReportDimension(value, newTotalsPropertyName, newSecondDimension);
      }
    }
  }
  return /*#__PURE__*/_jsx(Tooltip, {
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "reporting-enablement.reportEditors.overtimeTotalsEditor.tooltip.enabled"
    }),
    children: /*#__PURE__*/_jsx(ViewController, {
      size: "medium",
      value: isTimeSeriesReport(report) ? TIME_SERIES : AGGREGATION,
      onChange: onToggleChange,
      marginBlockEnd: "500",
      options: [{
        text: I18n.text('reporting-enablement.reportEditors.overtimeTotalsEditor.option.overtime'),
        value: TIME_SERIES
      }, {
        text: I18n.text('reporting-enablement.reportEditors.overtimeTotalsEditor.option.totals'),
        value: AGGREGATION
      }]
    })
  });
}